/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { ICustomer, ISearchCustomerReq } from '@/interfaces/customer';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { importCustomer } from '@/adapters/customer';
import { twMerge } from 'tailwind-merge';
import { isEmpty, isUndefined } from 'lodash';
import { getGeography } from '@/utils/geographyUtils';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchCustomerReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ImportCustomerModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchCustomerReq>({
    ..._criteria,
  });
  const [customer, setCustomer] = useState<ICustomer[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICustomer> | SorterResult<ICustomer>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof ICustomer) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<ICustomer>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'ชื่อลูกค้า',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      className: 'font-bold',
    },
    {
      title: 'เลขผู้เสียภาษี',
      dataIndex: 'taxId',
      key: 'taxId',
      width: 150,
    },
    {
      title: 'CT',
      dataIndex: 'creditTerm',
      key: 'creditTerm',
      className: 'font-medium',
      width: 80,
      align: 'center',
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitId',
      key: 'businessUnitId',
      className: 'font-medium',
      align: 'center',
      width: 80,
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'ถนน',
      dataIndex: 'road',
      key: 'road',
    },
    {
      title: 'ตำบล',
      dataIndex: 'subdistrictNameTh',
      key: 'subdistrictNameTh',
      width: 120,
    },
    {
      title: 'อำเภอ',
      dataIndex: 'districtNameTh',
      key: 'districtNameTh',
      width: 120,
    },
    {
      title: 'จังหวัด',
      dataIndex: 'provinceNameTh',
      key: 'provinceNameTh',
      width: 120,
    },
    {
      title: 'รหัสไปรษณีย์',
      dataIndex: 'postalCode',
      key: 'postalCode',
      width: 100,
    },
    {
      title: 'เบอร์โทรศัพท์',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Google Map',
      dataIndex: 'maplink',
      key: 'maplink',
      width: 100,
    },
    {
      title: 'Ref 1',
      dataIndex: 'reference1',
      key: 'reference1',
      width: 100,
    },
    {
      title: 'Ref 2',
      dataIndex: 'reference2',
      key: 'reference2',
      width: 100,
    },
    {
      title: 'Remark',
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
    },
    {
      title: 'WareHouse Remark',
      dataIndex: 'whRemark',
      key: 'whRemark',
      width: 120,
    },
    {
      title: 'Logistic Remark',
      dataIndex: 'logisticRemark',
      key: 'logisticRemark',
      width: 120,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setCustomer(c => c.filter(x => x.code !== rec.code))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setCustomer([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
        .map((c: any) => {
          const geo = getGeography(Number(c[13]));
          const customer: ICustomer = {
            code: c[0]?.toString() || '',
            name: c[1]?.toString() || '',
            taxId: c[2]?.toString() || '',
            creditTerm: Number(c[3] || 0),
            businessUnitId: Number(c[4]),
            address: c[5]?.toString() || '',
            road: c[6]?.toString() || '',
            // subdistrictNameEn: geo?.subdistrictNameEn,
            // subdistrictNameTh: geo?.subdistrictNameTh,
            districtNameEn: geo?.districtNameEn,
            districtNameTh: geo?.districtNameEn,
            provinceNameEn: geo?.provinceNameEn,
            provinceNameTh: geo?.provinceNameEn,
            subdistrictNameEn: c[7]?.toString() || '',
            subdistrictNameTh: c[8]?.toString() || '',
            // districtNameEn: c[9]?.toString() || '',
            // districtNameTh: c[10]?.toString() || '',
            // provinceNameEn: c[11]?.toString() || '',
            // provinceNameTh: c[12]?.toString() || '',
            postalCode: Number(c[13]),
            phoneNumber: c[14]?.toString() || '',
            maplink: c[15]?.toString() || '',
            reference1: c[16]?.toString() || '',
            reference2: c[17]?.toString() || '',
            remark: c[18]?.toString() || '',
            whRemark: c[19]?.toString() || '',
            logisticRemark: c[20]?.toString() || '',
            status: 1,
          } as ICustomer;
          return customer;
        });
      setCustomer(excelData);
      return false;
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutations = useMutation({
    mutationFn: async () => await importCustomer(customer),
    mutationKey: ['import-customer'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setCustomer([]);
        notification.success({
          message: 'นำเข้าลูกค้าสำเร็จ',
        });
        handleOk();
      } else {
        const duplicateCustomers: string[] = data.data?.data;
        const updateCustomer = customer.map(c => {
          c.status = duplicateCustomers.includes(c.code) ? 2 : 3;
          return c;
        });
        setCustomer(updateCustomer);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    mutations.mutateAsync();
  };

  return (
    <Modal
      title="นำเข้าลูกค้า"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<ICustomer>
          columns={columns}
          criteria={criteria}
          data={{
            data: customer,
            totalRecords: customer.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          // rowSelection={isMultiSelect ? rowSelection : undefined}
          scrollLength={3000}
          scroll
        />
      </div>
    </Modal>
  );
};

export default ImportCustomerModal;
